import type { Config } from "dompurify";
import DOMPurify from "dompurify";
import { containsHtmlMarkup } from "./plainTextFieldConstraints";

/**
 * Quill `formats` / toolbar capabilities: bold, italic, lists (ordered + bullet), links.
 * Keep toolbarOptions, useQuill `formats`, and validation in sync.
 */
export const RICHTEXT_QUILL_FORMATS = [
	"bold",
	"italic",
	"list",
	"link",
] as const;

export const RICHTEXT_QUILL_TOOLBAR_MODULES: Array<
	string | { list: "ordered" | "bullet" }
> = ["bold", "italic", { list: "ordered" }, { list: "bullet" }, "link"];

const ALLOWED_TAG_SET = new Set([
	"P",
	"BR",
	"STRONG",
	"B",
	"EM",
	"I",
	"UL",
	"OL",
	"LI",
	"A",
	// Quill 2 list items inject a UI span (see quill/formats/list.js attachUI).
	"SPAN",
]);

/** Stored HTML: only tags the toolbar can produce (+ structural p/br). */
export const RICH_TEXT_STORAGE_PURIFY: Config = {
	ALLOWED_TAGS: [
		"p",
		"br",
		"strong",
		"b",
		"em",
		"i",
		"ul",
		"ol",
		"li",
		"a",
		"span",
	],
	ALLOWED_ATTR: [
		"href",
		"target",
		"rel",
		"data-list",
		"class",
		"contenteditable",
	],
	// class / contenteditable: validation walk enforces per-tag rules.
};

/** String return; DOMPurify typings allow HTMLElement when RETURN_DOM is used. */
export function sanitizeRichTextForStorage(html: string): string {
	return DOMPurify.sanitize(html, RICH_TEXT_STORAGE_PURIFY) as string;
}

const DISALLOWED_MESSAGE =
	"This field only allows bold, italic, lists, and links. Remove unsupported formatting or content.";

const LITERAL_HTML_MESSAGE =
	"Don't type or paste raw HTML. Use the toolbar for bold, italic, lists, and links.";

const UNSAFE_LINK_MESSAGE =
	"Links must use safe http(s), mailto, or site paths. Remove unsafe URLs.";

function isAllowedQuillClass(value: string): boolean {
	return /^ql-[a-z0-9-]+$/i.test(value.trim());
}

function validateQuillClassOnlyAttributes(el: Element): string | null {
	for (let j = 0; j < el.attributes.length; j++) {
		const attr = el.attributes[j];
		const n = attr.name.toLowerCase();
		if (n.startsWith("on")) {
			return DISALLOWED_MESSAGE;
		}
		if (n === "class" && isAllowedQuillClass(attr.value)) {
			continue;
		}
		return DISALLOWED_MESSAGE;
	}
	return null;
}

/** Plain text as the user sees it (Quill getText or body.textContent of stored HTML). */
function normalizeEditorPlainText(plain: string): string {
	return plain
		.replace(/\u200b/g, "")
		.replace(/\n$/, "")
		.trimEnd();
}

export function htmlToPlainTextContent(html: string): string {
	const trimmed = (html || "").trim();
	if (!trimmed) {
		return "";
	}
	const doc = new DOMParser().parseFromString(trimmed, "text/html");
	return doc.body.textContent || "";
}

function isAllowedRichTextHref(href: string): boolean {
	const t = href.trim();
	if (!t) {
		return true;
	}
	const lower = t.toLowerCase();
	if (
		lower.startsWith("javascript:") ||
		lower.startsWith("vbscript:") ||
		lower.startsWith("data:")
	) {
		return false;
	}
	if (lower.startsWith("#")) {
		return true;
	}
	if (lower.startsWith("mailto:")) {
		return !/javascript:/i.test(t);
	}
	if (/^https?:\/\//i.test(t)) {
		return true;
	}
	if (t.startsWith("/") && !t.startsWith("//")) {
		return true;
	}
	try {
		const base =
			typeof window !== "undefined" && window.location?.origin
				? window.location.origin
				: "https://example.invalid";
		const u = new URL(t, base);
		return u.protocol === "http:" || u.protocol === "https:";
	} catch {
		return false;
	}
}

/**
 * Validates editor HTML against {@link RICHTEXT_QUILL_FORMATS} and rejects literal tag-like text
 * (typing "&lt;a href=…" shows as text in Quill but must not save) and unsafe link URLs.
 *
 * @param editorPlainText - `quill.getText()` live, or {@link htmlToPlainTextContent}(html) when loading stored value.
 */
export function validateRichTextToolbarFormats(
	html: string,
	editorPlainText: string,
): string | null {
	const normalizedPlain = normalizeEditorPlainText(editorPlainText);
	if (containsHtmlMarkup(normalizedPlain)) {
		return LITERAL_HTML_MESSAGE;
	}

	const trimmed = (html || "").trim();
	if (!trimmed) {
		return null;
	}
	if (
		/^<p>\s*<br\s*\/?>\s*<\/p>$/i.test(trimmed) ||
		/^<p>\s*<\/p>$/i.test(trimmed)
	) {
		return null;
	}

	const doc = new DOMParser().parseFromString(trimmed, "text/html");
	const elements = doc.body.querySelectorAll("*");

	for (let i = 0; i < elements.length; i++) {
		const el = elements[i];
		if (!ALLOWED_TAG_SET.has(el.tagName)) {
			return DISALLOWED_MESSAGE;
		}

		if (el.tagName === "A") {
			const href = el.getAttribute("href") || "";
			if (href && !isAllowedRichTextHref(href)) {
				return UNSAFE_LINK_MESSAGE;
			}
			for (let j = 0; j < el.attributes.length; j++) {
				const attr = el.attributes[j];
				const n = attr.name.toLowerCase();
				if (n.startsWith("on")) {
					return DISALLOWED_MESSAGE;
				}
				if (!["href", "target", "rel"].includes(n)) {
					return DISALLOWED_MESSAGE;
				}
			}
			continue;
		}

		if (el.tagName === "SPAN") {
			for (let j = 0; j < el.attributes.length; j++) {
				const attr = el.attributes[j];
				const n = attr.name.toLowerCase();
				if (n.startsWith("on")) {
					return DISALLOWED_MESSAGE;
				}
				if (n === "class" && isAllowedQuillClass(attr.value)) {
					continue;
				}
				if (n === "contenteditable" && attr.value.toLowerCase() === "false") {
					continue;
				}
				return DISALLOWED_MESSAGE;
			}
			continue;
		}

		if (el.tagName === "OL" || el.tagName === "UL") {
			const listErr = validateQuillClassOnlyAttributes(el);
			if (listErr) {
				return listErr;
			}
			continue;
		}

		if (el.tagName === "LI") {
			for (let j = 0; j < el.attributes.length; j++) {
				const attr = el.attributes[j];
				const n = attr.name.toLowerCase();
				if (n === "data-list") {
					const v = attr.value.trim();
					if (
						v !== "ordered" &&
						v !== "bullet" &&
						v !== "checked" &&
						v !== "unchecked"
					) {
						return DISALLOWED_MESSAGE;
					}
					continue;
				}
				if (n === "class" && isAllowedQuillClass(attr.value)) {
					continue;
				}
				return DISALLOWED_MESSAGE;
			}
			continue;
		}

		if (el.attributes.length > 0) {
			return DISALLOWED_MESSAGE;
		}
	}

	return null;
}
